import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import Spinner from 'react-bootstrap/Spinner';
import { useLocation, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getCurrMonth } from '../../shared/functions/dateProcessor';
import {
  filterStorageTypeMap,
  TABLE_REFETCH_INTERVAL_MILISEC,
  TrackerDataType,
  visibleContentLogCols,
} from '../../shared/constants/constants';
import isFilterEmpty from '../../shared/functions/isFilterEmpty';
import dayjs from '../../utils/dayjs';
import MonthSelector from '../TrackerTable/MonthSelector';
import TrackerTable from '../TrackerTable/TrackerTable';
import QueryAdderDrawer from '../QueryAdder/QueryAdderDrawer';
import QueryAdder from '../QueryAdder/QueryAdder';
import { DropdownTable, FlatContentLog, UserMeta } from '../../shared/types';
import { useFilters, useFiltersDispatch } from '../TrackerFilter/Context/FiltersContext';
import { useRecentCreations } from '../../shared/hooks/useRecentCreations';
import { getFilteredContentLogs, getMonthlyContentLogs } from '../../shared/requests/contentLogsRequest';
import { useContentLogHandler } from '../../shared/eventHandler/ContentLogEventHandler';
import { useServiceLogHandler } from '../../shared/eventHandler/ServiceLogEventHandler';
import TrackerTableFilter from '../TrackerTable/TrackerTableFilter';
import { getTrackerFilterQueryFromFilters } from '../TrackerFilter/trackerFilterHelpers';
import { TrackerTableActionButtons } from '../TrackerTable/TrackerTableActionButtons';
import TrackerTableFuzzySearch from '../TrackerTable/TrackerTableFuzzySearch';
import {
  logPageActionButtonsWrapperSx,
  logPageFilterContainerSx,
  logPageFilterWrapperSx,
  logPageTableContainerSx,
  logPageTableRegionSx,
} from '../../shared/styles/logPageLayoutStyles';

interface ContentLogPageProps {
  dropdownTable: DropdownTable | null;
  user: UserMeta;
}

export const ContentLogPage = ({ dropdownTable, user }: Readonly<ContentLogPageProps>) => {
  const filtersDispatch = useFiltersDispatch();
  const { state } = useLocation();
  const recentCreations = useRecentCreations();
  const { markCreated } = recentCreations;
  const previousMonthlyIds = useRef<Set<string> | null>(null);
  const [openForm, setOpenForm] = useState(false);

  const month = sessionStorage.getItem('contentLogCurrMonth') ?? getCurrMonth();
  const [currMonth, setCurrMonth] = useState(month);
  const [data, setData] = useState<FlatContentLog[]>([]);
  const [isBulkEdit, setIsBulkEdit] = useState(false);
  const [selectedBulkEditRowIds, setSelectedBulkEditRowIds] = useState<string[]>([]);
  const existingFilters = useFilters();
  const queryClient = useQueryClient();
  const contentLogParams = useParams();

  const onSuccessCallback = async () => {
    await queryClient.invalidateQueries({ queryKey: ['monthlyContentLogs'] });
    await queryClient.invalidateQueries({ queryKey: ['filteredContentLogs'] });
    if (state?.queryString && state?.category) {
      await queryClient.invalidateQueries({
        queryKey: ['fuzzySearch', TrackerDataType.CONTENT_LOG, state.queryString, state.category],
      });
    }
  };

  const contentLogEventHandler = useContentLogHandler({
    recentCreations,
    onSuccessCallback,
  });
  const serviceLogEventHandler = useServiceLogHandler({
    recentCreations,
    onSuccessCallback,
  });

  const {
    data: monthlyContentLogs,
    refetch: refetchMonthlyContentLogs,
    isLoading: isMonthlyContentLogsLoading,
  } = useQuery({
    queryKey: ['monthlyContentLogs', currMonth],
    queryFn: () => getMonthlyContentLogs(currMonth, dayjs.tz.guess()),
    enabled: !state && isFilterEmpty(existingFilters),
    refetchInterval: TABLE_REFETCH_INTERVAL_MILISEC,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  const filtersQueryString = useMemo(
    () => getTrackerFilterQueryFromFilters(existingFilters, TrackerDataType.CONTENT_LOG),
    [existingFilters]
  );

  const { data: filteredContentLogs } = useQuery({
    queryKey: ['filteredContentLogs', filtersQueryString],
    queryFn: async () => {
      const filteredData = await getFilteredContentLogs(filtersQueryString);
      return filteredData || [];
    },
    enabled: !!filtersQueryString,
  });

  const shouldDisableMonthSwitches = () => {
    const hasFilters = Object.keys(existingFilters).length > 0;
    const isQuickSearching = state?.queryString && state?.category;
    return hasFilters || isQuickSearching;
  };

  const detailedExportContext = useMemo(() => {
    if (state?.queryString && state?.category) {
      return {
        trackerType: TrackerDataType.CONTENT_LOG,
        mode: 'fuzzy' as const,
        queryString: state.queryString,
        category: state.category,
      };
    }

    if (!isFilterEmpty(existingFilters) && filtersQueryString) {
      return {
        trackerType: TrackerDataType.CONTENT_LOG,
        mode: 'filter' as const,
        filterQueryString: filtersQueryString,
      };
    }

    return {
      trackerType: TrackerDataType.CONTENT_LOG,
      mode: 'monthly' as const,
      month: currMonth,
      timezoneOffset: dayjs.tz.guess(),
    };
  }, [currMonth, existingFilters, filtersQueryString, state]);

  const selectAllBulkEditRows = (pageRowIds: string[]) => {
    setSelectedBulkEditRowIds(pageRowIds);
  };

  const clearBulkEditSelection = () => {
    setSelectedBulkEditRowIds([]);
  };

  const handleBulkEditSelectRow = (isChecked: boolean, rowId: string) => {
    setSelectedBulkEditRowIds((prevSelectedIds) => {
      if (isChecked) {
        return [...prevSelectedIds, rowId];
      } else {
        return prevSelectedIds.filter((id) => rowId !== id);
      }
    });
  };

  useEffect(() => {
    if (contentLogParams.contentLogId) {
      filtersDispatch({
        type: 'setFilters',
        payload: {
          id: contentLogParams.contentLogId,
        },
      });
    }
  }, [contentLogParams.contentLogId, filtersDispatch]);

  useEffect(() => {
    if (!monthlyContentLogs && !filteredContentLogs) {
      return;
    }
    const storedFilter = user.nonPIUserSettings?.userStoredFilters?.[filterStorageTypeMap[TrackerDataType.CONTENT_LOG]];
    if (!isFilterEmpty(existingFilters) && filteredContentLogs) {
      setData(filteredContentLogs);
    } else if (isFilterEmpty(existingFilters) && !state?.queryString && !storedFilter && monthlyContentLogs) {
      setData(monthlyContentLogs);
    }
  }, [
    monthlyContentLogs,
    filteredContentLogs,
    existingFilters,
    state,
    user.nonPIUserSettings?.userStoredFilters,
    filtersQueryString,
  ]);

  useEffect(() => {
    previousMonthlyIds.current = null;
  }, [currMonth]);

  useEffect(() => {
    if (!Array.isArray(monthlyContentLogs)) {
      return;
    }
    const storedFilter = user.nonPIUserSettings?.userStoredFilters?.[filterStorageTypeMap[TrackerDataType.CONTENT_LOG]];
    const shouldTrack = isFilterEmpty(existingFilters) && !state?.queryString && !storedFilter;

    if (shouldTrack && previousMonthlyIds.current) {
      const prevIds = previousMonthlyIds.current;
      monthlyContentLogs.forEach((row) => {
        if (!prevIds.has(row.id)) {
          markCreated(row.id);
        }
      });
    }

    previousMonthlyIds.current = new Set(monthlyContentLogs.map((row) => row.id));
  }, [monthlyContentLogs, existingFilters, state, user.nonPIUserSettings?.userStoredFilters, markCreated]);

  useEffect(() => {
    sessionStorage.setItem('contentLogCurrMonth', currMonth);
    if (!state && isFilterEmpty(existingFilters)) {
      refetchMonthlyContentLogs();
    }
  }, [currMonth, refetchMonthlyContentLogs, state, existingFilters]);

  const safeRows = Array.isArray(data) ? data : [];

  const monthSwitchesDisabled = shouldDisableMonthSwitches();
  const isMonthTransitionLoading = !monthSwitchesDisabled && isMonthlyContentLogsLoading;

  return (
    <Box sx={logPageTableContainerSx}>
      <Box>
        <MonthSelector
          disabled={monthSwitchesDisabled}
          currMonth={currMonth}
          onMonthChange={setCurrMonth}
          showTodayButton={false}
          showContainerCard={false}
          labelSx={{ fontWeight: 400 }}
        />
      </Box>
      <Box sx={logPageFilterContainerSx}>
        <Box sx={logPageFilterWrapperSx}>
          <TrackerTableFuzzySearch />
          {dropdownTable ? (
            <TrackerTableFilter
              setDataCallback={setData}
              eventHandler={contentLogEventHandler}
              dropdownTable={dropdownTable}
            />
          ) : null}
        </Box>
        <Box sx={logPageActionButtonsWrapperSx}>
          {dropdownTable ? (
            <TrackerTableActionButtons
              eventHandler={contentLogEventHandler}
              dropdownTable={dropdownTable}
              currMonth={currMonth}
              rows={safeRows}
              isBulkEdit={isBulkEdit}
              detailedExportContext={detailedExportContext}
              selectedBulkEditRowIds={selectedBulkEditRowIds}
              selectedBulkEditRowsCount={selectedBulkEditRowIds.length}
              onSwitchToBulkEditView={() => {
                setIsBulkEdit(true);
              }}
              onSwitchToMonthlyView={() => {
                setIsBulkEdit(false);
                clearBulkEditSelection();
              }}
              hasAutoAdd={false}
            />
          ) : null}
        </Box>
      </Box>
      {Array.isArray(data) && dropdownTable ? (
        <Box sx={{ ...logPageTableRegionSx, position: 'relative' }} aria-busy={isMonthTransitionLoading}>
          <TrackerTable
            rows={safeRows}
            dropdownTable={dropdownTable}
            visibleCols={visibleContentLogCols}
            eventHandler={contentLogEventHandler}
            linkedEventHandler={serviceLogEventHandler}
            isBulkEdit={isBulkEdit}
            selectedBulkEditRowIds={selectedBulkEditRowIds}
            onBulkEditSelectAll={(isChecked, pageRowIds) => {
              if (isChecked) {
                selectAllBulkEditRows(pageRowIds);
                return;
              }
              clearBulkEditSelection();
            }}
            onBulkEditSelectRow={handleBulkEditSelectRow}
            fitToContainer
          />
          {isMonthTransitionLoading ? (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 2,
                backgroundColor: 'rgba(251, 252, 254, 0.56)',
              }}
            >
              <div className="spinner-container">
                <Spinner animation="border" />
              </div>
            </Box>
          ) : null}
        </Box>
      ) : (
        <div className="spinner-container">
          <Spinner animation="border" />
        </div>
      )}
      {dropdownTable ? (
        <QueryAdderDrawer openForm={openForm} setOpenForm={setOpenForm} flush={true}>
          <QueryAdder
            title={contentLogEventHandler.newItemModalTitle}
            dropdownTable={dropdownTable}
            currMonth={currMonth}
            setOpenForm={setOpenForm}
            eventHandler={contentLogEventHandler}
          />
        </QueryAdderDrawer>
      ) : null}
    </Box>
  );
};
